import { useEffect, useMemo, useRef, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import type { LucideIcon } from "lucide-react";
import {
	Brain as FaBrain,
	ChartColumn as FaChartBar,
	ChevronDown as FaChevronDown,
	Gift as FaGift,
	House as FaHome,
	LogOut as FaSignOutAlt,
	Mail as FaEnvelope,
	MapPinned as FaMapMarkedAlt,
	Menu as FaBars,
	MessageSquare as FaRegCommentDots,
	Moon as FaMoon,
	Settings as FaCog,
	Share2 as FaShareAlt,
	Sun as FaSun,
	Target as FaBullseye,
	TriangleAlert as FaExclamationTriangle,
	X as FaTimes,
	Zap as FaBolt,
} from "lucide-react";
import splashIcon from "../../assets/images/splash-icon.png";
import splashIconDark from "../../assets/images/ToronQ Logos-15.png";
import { tokenService } from "../../services/tokenService";
import NotificationBell from "../components/NotificationBell";
import Button from "../../components/ui/Button";
import { useTheme } from "../../context/ThemeContext";
import useBusinessStore from "../../store/businessStore";
import { getProfilePictureApi } from "../../api/user.api";
import type { User } from "../../types/user";
import type { getTheme } from "../../config/theme";

type Theme = ReturnType<typeof getTheme>["colors"];

interface BannerProps {
	theme: Theme;
	onDismiss: () => void;
	onConnect: () => void;
}

function GoogleConnectReminderBanner({ theme, onDismiss, onConnect }: BannerProps) {
	return (
		<div
			className="mb-4 flex flex-col items-start justify-between gap-3 rounded-2xl border px-4 py-3 sm:flex-row sm:items-center"
			style={{ borderColor: theme.border, background: theme.infoBg }}
		>
			<div className="flex items-center gap-3">
				<FaExclamationTriangle size={18} color={theme.primary} />
				<p className="text-sm font-medium" style={{ color: theme.text }}>
					Your Google Business Profile isn't connected yet. Connect it to
					start receiving AI-drafted replies and synced reviews.
				</p>
			</div>

			<div className="flex shrink-0 items-center gap-2">
				<Button variant="primary" theme={theme} onClick={onConnect}>
					Connect Now
				</Button>

				<Button variant="tertiary" theme={theme} onClick={onDismiss}>
					Dismiss
				</Button>
			</div>
		</div>
	);
}

interface MenuItem {
	label: string;
	icon: LucideIcon;
	path: string;
	badge?: string | number;
}

interface MenuSection {
	title: string;
	items: MenuItem[];
}

const menuSections: MenuSection[] = [
	{
		title: "MAIN",
		items: [
			{ label: "Overview", icon: FaHome, path: "/dashboard/overview" },
			{
				label: "Reviews",
				icon: FaRegCommentDots,
				path: "/dashboard/reviews",
			},
			{
				label: "Locations",
				icon: FaMapMarkedAlt,
				path: "/dashboard/locations",
			},
			{ label: "Grow", icon: FaBolt, path: "/dashboard/grow" },
		],
	},
	{
		title: "AI FEATURES",
		items: [
			{
				label: "Reputation Score",
				icon: FaBullseye,
				path: "/dashboard/reputation-score",
			},
			{
				label: "Crisis Alerts",
				icon: FaExclamationTriangle,
				path: "/dashboard/crisis-alerts",
			},
			{
				label: "Review to Social",
				icon: FaShareAlt,
				path: "/dashboard/review-social",
			},
			{ label: "AEO Insights", icon: FaBrain, path: "/dashboard/aeo-insights" },
		],
	},
	{
		title: "INSIGHTS",
		items: [
			{
				label: "Competitors",
				icon: FaChartBar,
				path: "/dashboard/competitors",
			},
			{ label: "Referral", icon: FaGift, path: "/dashboard/referral" },
			{ label: "Settings", icon: FaCog, path: "/dashboard/settings" },
		],
	},
];

interface MenuButtonProps {
	item: MenuItem;
	pathname: string;
	onClick: (path: string) => void;
	theme: Theme;
}

function MenuButton({ item, pathname, onClick, theme }: MenuButtonProps) {
	const Icon = item.icon;
	const active = pathname === item.path;

	return (
		<button
			onClick={() => onClick(item.path)}
			className="relative flex w-full items-center justify-between rounded-xl px-3 py-2.5 text-left transition-colors"
			style={{
				background: active ? theme.infoBg : "transparent",
				color: active ? theme.primary : theme.textMuted,
			}}
		>
			{active && (
				<span
					className="absolute left-0 top-1/2 h-4 w-[3px] -translate-y-1/2 rounded-full"
					style={{ background: theme.primary }}
				/>
			)}

			<span className="flex items-center gap-3 text-sm font-medium" style={{ color: active ? theme.primary : theme.text }}>
				<Icon size={16} />
				{item.label}
			</span>

			{item.badge ? (
				<span
					className="rounded-full px-2 py-0.5 text-xs font-semibold text-white"
					style={{ background: theme.primary }}
				>
					{item.badge}
				</span>
			) : null}
		</button>
	);
}

export default function DashboardLayout() {
	const { theme, mode, isDark, toggleTheme } = useTheme();
	// Neutral border for structure — theme.border carries a blue tint meant for
	// accent surfaces; the shell uses it only where blue is intentional (active
	// nav state, the Google-connect banner) and a plain neutral everywhere else.
	const subtleBorder = mode === "dark" ? theme.border : "#E7EAF0";
	const [mobileOpen, setMobileOpen] = useState(false);
	const [accountMenuOpen, setAccountMenuOpen] = useState(false);
	const [bannerDismissed, setBannerDismissed] = useState(
		() => sessionStorage.getItem("google_connect_banner_dismissed") === "true",
	);
	const [profilePicture, setProfilePicture] = useState<string | null>(null);

	useEffect(() => {
		getProfilePictureApi()
			.then((res) => {
				const url =
					typeof res?.data === "string"
						? res.data
						: res?.data?.url || res?.url || null;
				if (url) setProfilePicture(url);
			})
			.catch(() => {});
	}, []);

	const { activeBusiness } = useBusinessStore();

	const location = useLocation();
	const navigate = useNavigate();
	const [user] = useState<User>(() =>
		JSON.parse(localStorage.getItem("user") || "{}"),
	);
	const accountMenuRef = useRef<HTMLDivElement>(null);

	const showGoogleConnectBanner =
		!bannerDismissed &&
		localStorage.getItem("google_connected") !== "true" &&
		(localStorage.getItem("google_connect_skipped") === "true" ||
			activeBusiness?.connectionStatus === "pending_oauth");

	const handleDismissGoogleBanner = () => {
		sessionStorage.setItem("google_connect_banner_dismissed", "true");
		setBannerDismissed(true);
	};

	const subPageTitles: Record<string, string> = {
		"/dashboard/help-center": "Help Center",
		"/dashboard/contact-support": "Contact Support",
		"/dashboard/legal/terms-and-conditions": "Terms & Conditions",
		"/dashboard/legal/privacy-policy": "Privacy Policy",

	};

	const pageTitle = useMemo(() => {
		const allItems = menuSections.flatMap((section) => section.items);
		return (
			allItems.find((item) => item.path === location.pathname)?.label ||
			subPageTitles[location.pathname] ||
			"Dashboard"
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [location.pathname]);

	const handleNavigate = (path: string) => {
		navigate(path);
		setMobileOpen(false);
		setAccountMenuOpen(false);
	};

	const handleLogout = () => {
		tokenService.clearTokens();
		localStorage.removeItem("user");
		localStorage.removeItem("isLoggedIn");
		localStorage.removeItem("selected_business");
		setAccountMenuOpen(false);
		navigate("/", { replace: true });
	};

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (
				accountMenuRef.current &&
				!accountMenuRef.current.contains(event.target as Node)
			) {
				setAccountMenuOpen(false);
			}
		};

		document.addEventListener("mousedown", handleClickOutside);

		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, []);

	return (
		<div
			className={`min-h-screen ${isDark ? "dark" : ""}`}
			style={{ background: theme.background }}
		>
			<div className="flex min-h-screen">
				{mobileOpen ? (
					<div
						className="fixed inset-0 z-40 bg-slate-900/45 xl:hidden"
						onClick={() => setMobileOpen(false)}
					/>
				) : null}

				<aside
					className={`fixed left-0 top-0 z-50 flex h-screen w-[300px] flex-col transition-transform duration-300 xl:translate-x-0 xl:border-r ${
						mobileOpen ? "translate-x-0" : "-translate-x-full"
					}`}
					style={{ borderColor: subtleBorder, background: theme.card }}
				>
					<div
						className="flex items-center justify-between border-b px-5 py-5"
						style={{ borderColor: subtleBorder }}
					>
						<div className="flex items-center gap-3">
							<img
								src={mode === "dark" ? splashIconDark : splashIcon}
								alt="ToronQ logo"
								className="mx-auto h-11 w-auto object-contain drop-shadow-lg"
							/>
						</div>

						<button
							className="flex h-10 w-10 items-center justify-center rounded-xl xl:hidden"
							style={{ background: theme.infoBg }}
							onClick={() => setMobileOpen(false)}
						>
							<FaTimes size={16} color={theme.text} />
						</button>
					</div>

					<div className="flex-1 overflow-y-auto px-3 py-4">
						{menuSections.map((section) => (
							<div key={section.title} className="mb-6">
								<p
									className="px-3 pb-2 text-xs font-medium"
									style={{ color: theme.textLight }}
								>
									{section.title}
								</p>

								<div className="space-y-1">
									{section.items.map((item) => (
										<MenuButton
											key={item.path}
											item={item}
											pathname={location.pathname}
											onClick={handleNavigate}
											theme={theme}
										/>
									))}
								</div>
							</div>
						))}
					</div>

					<div
						ref={accountMenuRef}
						className="relative border-t p-4"
						style={{ borderColor: subtleBorder }}
					>
						<button
							type="button"
							onClick={() => setAccountMenuOpen((open) => !open)}
							className="flex w-full items-center gap-2 rounded-xl px-1 py-1 text-left transition-colors"
						>
							<div
								className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-xl text-sm font-semibold text-white"
								style={{ background: theme.primary }}
							>
								{profilePicture ? (
									<img
										src={profilePicture}
										alt="Profile"
										className="h-full w-full object-cover"
										onError={() => setProfilePicture(null)}
									/>
								) : (
									(user?.fullName || user?.full_name || user?.firstname || "U")
										.charAt(0)
										.toUpperCase()
								)}
							</div>

							<div className="min-w-0 flex-1">
								<p
									className="truncate text-sm font-semibold"
									style={{ color: theme.text }}
								>
									{user?.fullName ||
										user?.full_name ||
										[user?.firstname, user?.lastname]
											.filter(Boolean)
											.join(" ") ||
										"User"}
								</p>

								<div
									className="flex items-center gap-1 text-xs"
									style={{ color: theme.textMuted }}
								>
									<FaEnvelope size={10} />
									<span className="truncate">
										{user?.email || "No email"}
									</span>
								</div>
							</div>
							<FaChevronDown
								size={12}
								className={`shrink-0 transition-transform ${
									accountMenuOpen ? "rotate-180" : ""
								}`}
								style={{ color: theme.textLight }}
							/>
						</button>

						{accountMenuOpen ? (
							<div
								className="absolute bottom-[76px] right-2 z-20 rounded-2xl border p-2 shadow-[0_18px_45px_rgba(15,23,42,0.12)]"
								style={{ borderColor: subtleBorder, background: theme.card }}
							>
								<button
									type="button"
									onClick={handleLogout}
									className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition"
									style={{ color: theme.textMuted }}
								>
									<FaSignOutAlt size={13} />
									Log out
								</button>
							</div>
						) : null}
					</div>
				</aside>

				<div className="flex min-w-0 flex-1 flex-col xl:ml-[300px]">
					<header
						className="fixed left-0 right-0 top-0 z-30 border-b backdrop-blur xl:left-[300px]"
						style={{
							borderColor: subtleBorder,
							background: mode === "dark" ? "rgba(11,18,32,0.9)" : "rgba(255,255,255,0.9)",
						}}
					>
						<div className="flex items-center justify-between gap-4 px-4 py-4 sm:px-6 lg:px-8">
							<div className="flex items-center gap-3">
								<button
									className="flex h-11 w-11 items-center justify-center rounded-2xl border xl:hidden"
									style={{ borderColor: subtleBorder, background: theme.card }}
									onClick={() => setMobileOpen(true)}
								>
									<FaBars size={16} color={theme.text} />
								</button>

								<div>
									<h2
										className="text-xl font-semibold tracking-tight sm:text-2xl"
										style={{ color: theme.text }}
									>
										{pageTitle}
									</h2>

									<p className="text-sm" style={{ color: theme.textMuted }}>
										Manage your business reputation dashboard
									</p>
								</div>
							</div>

							<div className="hidden items-center gap-3 md:flex">
								<button
									type="button"
									onClick={toggleTheme}
									className="flex h-11 w-11 items-center justify-center rounded-2xl transition"
										style={{
											border: `1.5px solid ${subtleBorder}`,
											background: mode === "dark" ? "rgba(255,255,255,0.08)" : theme.card,
										}}
										aria-label={mode === "dark" ? "Switch to light mode" : "Switch to dark mode"}
										title={mode === "dark" ? "Switch to light mode" : "Switch to dark mode"}
									>
										{mode === "dark" ? (
											<FaSun size={16} color="#FBBF24" />
										) : (
											<FaMoon size={16} color={theme.primary} />
									)}
								</button>

								<NotificationBell theme={theme} />
							</div>
						</div>
					</header>

					<main className="flex-1 px-4 pb-6 pt-[96px] sm:px-6 lg:px-8">
						{showGoogleConnectBanner ? (
							<GoogleConnectReminderBanner
								theme={theme}
								onDismiss={handleDismissGoogleBanner}
								onConnect={() => navigate("/connect-google")}
							/>
						) : null}
						<Outlet />
					</main>
				</div>
			</div>
		</div>
	);
}
